// Physiological rate engine (BIOLOGY-ENGINE.md §1–2). Pure: give it a bird, an
// environment and a duration, get the same result every time. All rates are
// per sim-hour, so a coarse offline tick and a live 1Hz tick agree exactly.

import { clamp } from '../core/math';
import type { ChickenEntity, Physiology } from '../core/types';
import type { BiologyConfig } from './config';
import { isSilkieFeathered } from './genetics';

/** Everything outside the bird that changes how fast its state moves. */
export interface EnvContext {
  tempF: number;
  /** Rain or storm overhead and the bird is not under a roof. */
  wetAndExposed: boolean;
  /** Shade or coop roof available against the sun. */
  sheltered: boolean;
  hasWater: boolean;
  waterPurity: number;
  hasFeed: boolean;
  /**
   * Share of the step the bird reached drinkable water (PLAN-04 S4). Undefined
   * reads as 1, so a caller that has not rolled access yet — and every test
   * that builds an EnvContext by hand — behaves as it always did.
   */
  waterAccess?: number;
  /** Share of the step the bird reached the feed, same default. */
  feedAccess?: number;
  /**
   * How much the bird wants relative to an adult layer: NUT-10 by stage and
   * NUT-11 under heat for water, BRD-16/17/18 by class for feed. Undefined
   * reads as 1.
   */
  waterNeedScale?: number;
  feedNeedScale?: number;
  /** Feed loaded matches the bird's life stage. */
  feedCorrectForStage: boolean;
  litterDirtiness: number;
  /** Birds over the coop's rated capacity. */
  overcrowdedBy: number;
  /** On open ground where the bird can forage. */
  canForage: boolean;
  roosting: boolean;
  hasPerch: boolean;
  hasGrit: boolean;
  isLaying: boolean;
  /** Brooder thermal stress index for chicks, if brooded (GENETICS.md §1.2). */
  brooderTsi?: number;
}

export function createPhysiology(): Physiology {
  return {
    hydration: 90,
    energyReserve: 85,
    bodyCondition: 70,
    coreTemp: 105.5,
    stress: 15,
    immune: 80,
    gutHealth: 90,
  };
}

/** Hydration drain multiplier for the current heat and activity. */
export function hydrationMultiplier(
  env: EnvContext,
  config: BiologyConfig,
  isLaying: boolean,
  roosting: boolean,
): number {
  const h = config.hydration;
  let m = 1;
  if (env.tempF > config.temperature.severeHeatF) m *= h.heatMultiplier92F;
  else if (env.tempF > config.temperature.comfortHighF) m *= h.heatMultiplier85F;
  if (isLaying) m *= h.layingMultiplier;
  if (roosting) m *= h.nightRoostMultiplier;
  // Shade blunts the worst of tropical afternoon heat.
  if (env.sheltered && env.tempF > config.temperature.comfortHighF) m *= 0.75;
  return m;
}

export function energyMultiplier(
  env: EnvContext,
  config: BiologyConfig,
  isChick: boolean,
  isLaying: boolean,
): number {
  const e = config.energy;
  let m = 1;
  if (isChick) m *= e.chickMultiplier;
  if (env.tempF < config.temperature.comfortLowF || env.wetAndExposed) {
    m *= e.coldStressMultiplier;
  }
  if (isLaying) m *= e.layingMultiplier;
  if (env.canForage) m *= e.foragingMultiplier;
  return m;
}

/**
 * The bird's target immune level given how it is being kept. Immunity drifts
 * toward this over ~72h rather than snapping — recovery takes real days.
 */
export function immuneTarget(
  physiology: Physiology,
  env: EnvContext,
  config: BiologyConfig,
): number {
  const i = config.immune;
  const stressScore = 100 - physiology.stress;
  const sanitationScore = 100 - env.litterDirtiness;
  const nutritionScore = physiology.bodyCondition;
  return clamp(
    stressScore * i.stressWeight +
      sanitationScore * i.sanitationWeight +
      nutritionScore * i.nutritionWeight,
    0,
    100,
  );
}

/** Effective ambient temperature; wet plumage in the wind chills a bird hard. */
export function effectiveTempF(
  chicken: ChickenEntity,
  env: EnvContext,
  config: BiologyConfig,
): number {
  if (!env.wetAndExposed) return env.tempF;
  // A Silkie's fur-like ff plumage is not waterproof — she soaks and chills.
  const penalty = config.temperature.wetChillPenaltyF * (isSilkieFeathered(chicken.genotype) ? 2 : 1);
  return env.tempF - penalty;
}

export interface StepOptions {
  hours: number;
  config: BiologyConfig;
  isChick: boolean;
  /** Normal mode floors stats so neglect never kills (locked decision #11). */
  floorAt?: number;
}

/**
 * Advances one bird's physiology. Mutates in place — the caller owns the state
 * object — and rounds every stored value to 2dp (AGENT.md §3).
 */
export function stepPhysiology(
  chicken: ChickenEntity,
  env: EnvContext,
  options: StepOptions,
): void {
  const { hours, config, isChick } = options;
  const p = chicken.physiology;
  const floor = options.floorAt ?? 0;

  // --- Hydration: the fastest danger ---
  // A bird that only reached the water part of the hour drinks that part and
  // goes thirsty for the rest. Weighting the loss by the share she missed is
  // what makes being kept off the drinker an actual cost: before the access
  // model, "water exists" was the whole question, so a low-ranking hen in a
  // yard with a full waterer never ran short at all.
  const drink = env.hasWater && env.waterPurity > 35;
  const waterAccess = clamp(env.waterAccess ?? 1, 0, 1);
  const waterNeed = env.waterNeedScale ?? 1;
  if (drink) {
    const loss = config.hydration.lossPerHour * hydrationMultiplier(env, config, env.isLaying, env.roosting);
    p.hydration +=
      (waterAccess * config.hydration.drinkPerHour * waterNeed - (1 - waterAccess) * loss) * hours;
  } else {
    p.hydration -=
      config.hydration.lossPerHour *
      hydrationMultiplier(env, config, env.isLaying, env.roosting) *
      hours;
  }

  // --- Energy: days, not hours ---
  const fed = env.hasFeed;
  const feedAccess = clamp(env.feedAccess ?? 1, 0, 1);
  const feedNeed = env.feedNeedScale ?? 1;
  if (fed) {
    // Gut damage cuts absorption; grit improves it when there is grain to grind.
    const absorption =
      (p.gutHealth < 60 ? 1 - config.gut.damagedAbsorptionPenalty : 1) *
      (env.hasGrit ? 1 + config.gut.gritAbsorptionBonus : 1);
    const loss = config.energy.lossPerHour * energyMultiplier(env, config, isChick, env.isLaying);
    p.energyReserve +=
      (feedAccess * config.energy.eatPerHour * feedNeed * absorption - (1 - feedAccess) * loss) *
      hours;
  } else {
    p.energyReserve -=
      config.energy.lossPerHour * energyMultiplier(env, config, isChick, env.isLaying) * hours;
  }

  // --- Body condition: weeks, driven by sustained adequacy ---
  const bc = config.bodyCondition;
  let bodyDelta =
    p.energyReserve >= bc.adequateEnergyThreshold ? bc.maxChangePerHour : -bc.maxChangePerHour;
  if (!env.feedCorrectForStage) bodyDelta -= bc.maxChangePerHour * bc.wrongFeedPenalty;
  p.bodyCondition += bodyDelta * hours;

  // --- Core temperature: homeostasis against the effective ambient ---
  const ambient = effectiveTempF(chicken, env, config);
  const t = config.temperature;
  let tempDrift = 0;
  if (ambient > t.severeHeatF) tempDrift = 0.35;
  else if (ambient > t.pantingThresholdF) tempDrift = 0.15;
  else if (ambient < t.comfortLowF) tempDrift = -0.2;
  if (env.brooderTsi !== undefined && env.brooderTsi > config.brooder.optimalTsiF) {
    // Chicks cannot thermoregulate; brooder error hits core temp directly.
    tempDrift += (env.brooderTsi / 10) * (ambient < t.coreTempNormalF ? -1 : 1);
  }
  // Always pulled back toward normal — the bird is actively regulating.
  const towardNormal = (t.coreTempNormalF - p.coreTemp) * 0.25;
  p.coreTemp += (tempDrift + towardNormal) * hours;

  // --- Stress ---
  const s = config.stress;
  let stressDelta = -s.decayPerHour;
  if (env.roosting && env.hasPerch) stressDelta -= s.roostingBonusPerHour * s.perchStressReduction;
  if (env.overcrowdedBy > 0) stressDelta += s.overcrowdingPerBirdPerHour * env.overcrowdedBy;
  if (!drink) stressDelta += 0.5;
  if (ambient > t.severeHeatF || env.wetAndExposed) stressDelta += 1.0;
  p.stress += stressDelta * hours;

  // --- Immunity: drifts toward what husbandry deserves ---
  const target = immuneTarget(p, env, config);
  const driftRate = hours / config.immune.driftHours;
  p.immune += (target - p.immune) * clamp(driftRate, 0, 1);

  // --- Gut health ---
  const hasCoccidiosis = chicken.conditions.some((c) => c.id === 'coccidiosis');
  p.gutHealth += hasCoccidiosis
    ? -config.gut.coccidiosisDamagePerHour * hours
    : config.gut.recoveryPerHour * hours;

  clampPhysiology(p, floor);
}

/**
 * Clamps every value into range.
 *
 * Deliberately does NOT round. These are accumulators, and a live tick is
 * 1/3600 of an hour: body condition moves at most 0.1 per hour, so one tick
 * adds 0.0000278 in Real-Time and 0.00267 in Simulation. Rounding to 2dp threw
 * that away before the next tick could add to it, so body condition never moved
 * during play at all — a hen could only recover by closing the tab, because the
 * coarse quarter-hour steps of offline catch-up were the only ones large enough
 * to survive the rounding. Since laying probability scales directly with body
 * condition, that alone flattened egg production to near zero.
 *
 * This is the same bug already fixed in bird ageing, the world clock, litter
 * soiling, water and feed drain and coop dirt. Rounding belongs at the display
 * edge, never on a value the next tick reads back.
 */
export function clampPhysiology(p: Physiology, floor = 0): void {
  p.hydration = clamp(p.hydration, floor, 100);
  p.energyReserve = clamp(p.energyReserve, floor, 100);
  p.bodyCondition = clamp(p.bodyCondition, floor, 100);
  p.coreTemp = clamp(p.coreTemp, 90, 115);
  p.stress = clamp(p.stress, 0, 100);
  p.immune = clamp(p.immune, floor, 100);
  p.gutHealth = clamp(p.gutHealth, floor, 100);
}

export type LadderLevel = 'fine' | 'reduced' | 'lethargic' | 'critical';

/**
 * Consequence ladder (BIOLOGY-ENGINE.md §2) — one shared shape for hydration
 * and energy, so symptoms and UI copy stay consistent between them.
 */
export function ladderLevel(value: number): LadderLevel {
  if (value > 60) return 'fine';
  if (value > 40) return 'reduced';
  if (value > 20) return 'lethargic';
  return 'critical';
}

/** Laying pauses below the "reduced" rung — a real, teachable consequence. */
export function canLay(physiology: Physiology): boolean {
  return ladderLevel(physiology.hydration) === 'fine' && physiology.energyReserve > 40;
}
